import { Component, ElementRef, ViewChild, Input, Renderer2, SimpleChanges, Output, EventEmitter, OnChanges, OnInit } from '@angular/core';
import {
	BarController,
	BarElement,
	CategoryScale,
	Chart,
	ChartArea,
	ChartConfiguration,
	ChartDataset,
	Legend,
	LineController,
	LinearScale,
	LineElement,
	PointElement,
	Tooltip,
	TooltipItem,
	Tick,
	TooltipOptions,
	LegendItem,
	Color,
	TooltipModel
} from 'chart.js';
import { BbBarLabel, BbBarGoalLine, BB_BAR_LABEL_ID, BB_BAR_GOAL_LINE_ID } from './plugins';
import { BLUE, GREEN, ORANGE } from '../../analytics/project/project-stats-performance.component';
// import 'chartjs-plugin-style';

const BB_COLOR = '#7ab1df';

interface BbChartOptions {
	display?: boolean;
}

interface BbBarLabelOptions extends BbChartOptions {
	toFixed?: number;
}

interface BbBarGoalLineOptions extends BbChartOptions {
	goal?: number;
}

interface ChartToolTip extends TooltipItem<'bar'> {
	dataPoint: any;
}

export interface BbChartSettings {
	xAxisLabels?: string[];
	xAxisAutoSkipPadding?: number;
	labelX?: string; // title below X axis, e.g. 'date'
	labelY?: string; // title next to Y axis, e.g. 'views'
	maxTicksLimitY?: number;
	maxTicksLimitX?: number;
	stepSizeX?: number;
	stepSizeY?: number;
	axisTypeX?: any; // 'linear' | 'logarithmic' | 'category' | 'time' | 'timeseries' | 'radialLinear' ?
	bodyColor?: string;
	scaleYSuggestedMax?: number;
	maxLabelRotation?: number;
	showLegend?: boolean;
	padding?: number | {
		left?: number;
		right?: number;
		top?: number;
		bottom?: number;
	};
	tooltipTitleColor?: string;
	tooltipFirstItem?: string; // display extra information behind tooltip title (first item)
	tooltipLastItem?: string; // display extra information behind tooltip title (last item)
}

export interface BbPluginOptions {
	tooltip?: TooltipOptions<'bar'>;
	bbBarLabel?: BbBarLabelOptions;
	bbBarGoalLine?: BbBarGoalLineOptions;
}

export interface ChartData {
	x: number;
	y: number;
	tooltipTitle?: string;
}

@Component({
	selector: 'bb-chart',
	templateUrl: './bb-chart.component.html',
	styleUrls: ['./bb-chart.component.scss']
})

export class BbChartComponent implements OnInit, OnChanges {
	@ViewChild('canvasContainer', { static: true }) canvasContainerElementRef: ElementRef;
	@ViewChild('chart', { static: true }) chartElementRef: ElementRef;

	@Input() type: 'line' | 'bar';
	@Input() datasets: ChartDataset[];
	@Input() ticksCallbackX: (tickValue: any, index: number, ticks: Tick[]) => string;
	@Input() width: number;
	@Input() height: number;
	@Input() pluginOptions: BbPluginOptions;
	@Input() responsiveSizing = false;
	@Input() bbChartSettings: BbChartSettings;
	@Input() styling: string;

	@Output() canvasContextChange: EventEmitter<CanvasRenderingContext2D> = new EventEmitter<CanvasRenderingContext2D>();
	@Output() chartAreaChange: EventEmitter<ChartArea> = new EventEmitter<ChartArea>();

	chartContext: CanvasRenderingContext2D;
	chart: Chart;
	chartConfiguration: ChartConfiguration;
	initializedChartData: boolean;
	labelXlimit: number;

	constructor(
		private renderer: Renderer2
	) {
		if (!Chart.registry.plugins.get(BB_BAR_LABEL_ID)) {
			Chart.register(
				BarController,
				BarElement,
				CategoryScale,
				Legend,
				LineController,
				LineElement,
				LinearScale,
				PointElement,
				Tooltip,
				BbBarLabel,
				BbBarGoalLine
			);
		}

		this.chartConfiguration = {
			type: 'line',
			data: {
				labels: [],
				datasets: []
			},
			options: {
				plugins: {}
			}
		};
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes) {
			if (changes.datasets) {
				this.initializedChartData = false;
				if (changes.datasets.currentValue && changes.datasets.currentValue.length) {
					this.initChartWithData(changes.datasets.currentValue);
				}
			}
			if (changes.pluginOptions) {
				const pluginOptions = changes.pluginOptions.currentValue;
				if (!this.chartConfiguration) {
					return;
				}
				if (!this.chartConfiguration.options.plugins) {
					this.chartConfiguration.options.plugins = {};
				}
				if (pluginOptions && this.chartConfiguration && this.chartConfiguration.options.plugins) {
					[BB_BAR_LABEL_ID, BB_BAR_GOAL_LINE_ID].forEach((pluginName: string) => {
						if (pluginName in pluginOptions) {
							this.chartConfiguration.options.plugins[pluginName] = pluginOptions[pluginName];
						}
					});

					if (this.chart) {
						this.chart.update();
					}
				}
			}
			if (changes.bbChartSettings && this.bbChartSettings) {
				this.updateSettings();
			}
			if (this.chart) {
				this.chart.update();
			}
		}
	}

	updateSettings() {
		if (!this.chart || !this.bbChartSettings) {
			return;
		}

		if (this.bbChartSettings.scaleYSuggestedMax) {
			(this.chart.options.scales.y as any).suggestedMax = this.bbChartSettings.scaleYSuggestedMax;
			this.chart.update();
		}

		if (this.bbChartSettings.maxTicksLimitX) {
			(this.chart.options.scales.x.ticks as any).maxTicksLimit = this.bbChartSettings.maxTicksLimitX;
			this.chart.update();
		}

		if (this.bbChartSettings.padding) {
			this.chart.options.layout.padding = this.bbChartSettings.padding;
			this.chart.update();
		}
	}

	public updateSize() {
		this.renderer.setStyle(this.canvasContainerElementRef.nativeElement, 'width', !this.width ? '100%' : this.width + 'px');
		this.renderer.setStyle(this.canvasContainerElementRef.nativeElement, 'height', !this.height ? '100%' : this.height + 'px');
	}

	// Determine which X-labels to show in order to always show first and last
	determineMaxTicksLimitX(): void {
		const canvasWidth = this.canvasContainerElementRef.nativeElement.clientWidth;
		if (!canvasWidth || canvasWidth === 0 || this.bbChartSettings.maxTicksLimitX) {
			// graph not ready or limits set by parent component
			return;
		}

		const limit = Math.ceil(canvasWidth / 155) + 1; // 155px assumed max-width of label
		if (this.labelXlimit === limit) {
			return;
		}

		// maxTicksLimit has issues with always displaying the last value and right border // https://github.com/chartjs/Chart.js/issues/8745
		this.chartConfiguration.options.scales.x.ticks.callback = function(val: number, index, values) {
			const value = this.getLabelForValue(val);
			const splitIndex = value.indexOf(' - ');
			// only show first and last value and those that fit the limit (but not too close to last label)
			const everyX = Math.ceil(values.length / limit);
			if (index === 0 || index === values.length - 1 || (index % everyX === 0 && index <= values.length - everyX)) {
				return splitIndex >= 0 ? value.substr(0, splitIndex) : value;
			}
		};
		this.labelXlimit = limit;
	}

	ngOnInit() {
		if (this.type === undefined) {
			this.type = 'line';
		}
		if (this.bbChartSettings.maxTicksLimitY === undefined) {
			this.bbChartSettings.maxTicksLimitY = 5;
		}

		this.updateSize();

		this.chartContext = this.chartElementRef.nativeElement.getContext('2d');
		this.canvasContextChange.emit(this.chartContext);

		// Using any here because it's not possible to use typing here
		const plugins: any = this.chartConfiguration.options.plugins;
		const axisColor = this.styling === 'analytics' ? '#555555' : '#7AB1DF';

		this.chartConfiguration = {
			type: this.type,
			data: {
				labels: this.getLabels(this.datasets),
				datasets: this.datasets
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				layout: {
					padding: this.type === 'bar' && !this.bbChartSettings?.padding ? 0 : this.bbChartSettings?.padding || 0
				},
				interaction: {
					mode: 'index',
					intersect: false,
				},
				onResize: () => {
					this.onChartResize();
				},
				scales: {
					x: {
						ticks: {
							color: axisColor,
							maxRotation: this.bbChartSettings.maxLabelRotation, // X
							// padding between labels on x axis
							autoSkipPadding: this.bbChartSettings.xAxisAutoSkipPadding ? this.bbChartSettings.xAxisAutoSkipPadding : 0,
							callback(val: number) {
								const value = this.getLabelForValue(val);
								// return part before ' - ' or all. e.g. '20 jun - 28 jun'
								const splitIndex = value.indexOf(' - ');
								return splitIndex >= 0 ? value.substr(0, splitIndex) : value;
							}
						}
					},
					y: {
						display: true,
						ticks: {
							color: axisColor,
							maxTicksLimit: this.bbChartSettings.maxTicksLimitY
						},
						beginAtZero: true
					}
				},
				hover: {
					intersect: false,
				},
				elements: {
					point: {
						radius: 0,
						hoverRadius: 4,
					}
				},
			}
		};

		if (this.bbChartSettings.axisTypeX) {
			this.chartConfiguration.options.scales.x.type = this.bbChartSettings.axisTypeX;
		}

		if (this.bbChartSettings.labelX) {
			(this.chartConfiguration.options.scales.x as any).title = {
				display: this.bbChartSettings.labelX !== undefined,
				text: this.bbChartSettings.labelX
			};
		}

		if (this.bbChartSettings.labelY) {
			(this.chartConfiguration.options.scales.y as any).title = {
				display: this.bbChartSettings.labelY !== undefined,
				text: this.bbChartSettings.labelY
			};
		}

		if (this.styling === 'analytics') {
			this.chartConfiguration.options.scales.x.grid = {
				color: 'rgba(216, 229, 238, 0.35)',
				drawBorder: false
			};

			this.chartConfiguration.options.scales.y.grid = {
				color: '#d8e5ee',
				drawBorder: false
			};
		}

		this.chartConfiguration.options.plugins = plugins;
		if (!plugins.tooltip) {
			plugins.tooltip = {
				enabled: false,
				position: 'nearest',
				mode: 'index', // show all Y-values in tooltip at this X-point on hover
				intersect: false,
				// following lines activate the custom tooltip.
				// Example from https://github.com/chartjs/Chart.js/blob/master/docs/samples/tooltip/html.md
				// https://www.chartjs.org/docs/3.3.2/samples/tooltip/html.html
				external: (s) => {
					this.externalTooltipHandler(s);
				}
			};
		}

		this.chartConfiguration.options.plugins.legend = {
			display: !!this.bbChartSettings.showLegend
		};

		if (this.type === 'bar' || this.pluginOptions) {
			if (this.pluginOptions && this.pluginOptions.bbBarLabel) {
				this.pluginOptions.bbBarLabel.display = true;
				(this.chartConfiguration.options.plugins as BbPluginOptions).bbBarLabel = this.pluginOptions.bbBarLabel;
			} else {
				(this.chartConfiguration.options.plugins as BbPluginOptions).bbBarLabel = { display: true };
			}
		} else {
			(this.chartConfiguration.options.plugins as BbPluginOptions).bbBarLabel = { display: false };
		}

		if (this.ticksCallbackX !== undefined) {
			this.chartConfiguration.options.scales.x.ticks.callback = this.ticksCallbackX;
		}

		this.chart = new Chart(this.chartContext, this.chartConfiguration);
		this.updateSettings();
		this.initChartWithData(this.chartConfiguration.data.datasets);
	}

	onChartResize() {
		this.determineMaxTicksLimitX();
		if (this.chart) {
			this.chartAreaChange.emit(this.chart.chartArea);
		}
	}

	initChartWithData(datasets: ChartDataset[]) {
		if (this.chart && !this.initializedChartData) {
			if (datasets && datasets.length) {
				if (datasets[0]?.data?.length) {
					const data: ChartData[] = datasets[0].data as ChartData[];
					if (data[0].tooltipTitle !== undefined) {
						this.addTooltipPlugin();
					}
				} else {
					if (this.chartConfiguration && this.chartConfiguration.options && this.chartConfiguration.options.plugins) {
						delete this.chartConfiguration.options.plugins.tooltip;
					}
				}
				if (datasets[0]?.label) {
					this.addLegendPlugin();
				} else {
					this.chartConfiguration.options.plugins.legend = { display: !!this.bbChartSettings.showLegend };
				}
				datasets?.forEach((dataset: ChartDataset) => {
					if (!dataset.borderColor) { // line graphs
						dataset.borderColor = [BB_COLOR];
					}
					if (!dataset.backgroundColor) { // bar charts
						dataset.backgroundColor = [BB_COLOR];
					}
					if (!dataset.borderWidth) {
						dataset.borderWidth = 1;
					}
				});
			}

			this.chartConfiguration.data.labels = this.getLabels(datasets);
			this.chart.data.datasets = datasets;

			// on mobile: smaller skippadding to always show x labels
			if (this.bbChartSettings.xAxisAutoSkipPadding && (this.chart.chartArea.right - this.chart.chartArea.left) < 500) {
				(this.chartConfiguration.options.scales.x.ticks as any).autoSkipPadding = 20;
			} else {
				(this.chartConfiguration.options.scales.x.ticks as any).autoSkipPadding = this.bbChartSettings.xAxisAutoSkipPadding;
			}
			this.determineMaxTicksLimitX();
			this.chart.update();
			setTimeout(() => {
				// TODO: find a better way..
				this.chartAreaChange.emit(this.chart.chartArea);
			}, 1000);
			this.initializedChartData = true;
		}
	}

	addLegendPlugin() {
		this.chartConfiguration.options.plugins.legend = {
			display: true,
			labels: {
				boxWidth: 12, // (box is the colored square)
				boxHeight: 12,
				padding: 12,
				pointStyle: 'rect',
				generateLabels: (chart) => {
					const items = Chart.defaults.plugins.legend.labels.generateLabels(chart);
					items.forEach(item => {
						item.borderRadius = 2;
					});
					return items;
				}
			}
		};

		if (!this.bbChartSettings || this.bbChartSettings.showLegend !== true) {
			const color: Color = 'rgba(0,0,0,0)';

			this.chartConfiguration.options.plugins.legend.labels.generateLabels = () => {
				const legendItem: LegendItem = { text: '', datasetIndex: 0, fillStyle: color, strokeStyle: color };
				return [legendItem] as LegendItem[];
			};
		}
	}

	addTooltipPlugin() {
		(this.chartConfiguration.options.plugins.tooltip.callbacks as any) = {
			title: (tooltipItems: ChartToolTip[]) => {
				if (tooltipItems && tooltipItems.length && tooltipItems[0].dataPoint) {
					const dataPoint = tooltipItems[0].dataPoint;
					return dataPoint.tooltipTitle;
				}
				return '';
			}
		};

		this.setTooltipColors();

		this.chart.update();
	}

	getLabels(datasets: ChartDataset[]) {
		if (this.bbChartSettings.xAxisLabels) {
			return this.bbChartSettings.xAxisLabels;
		}
		if (datasets && datasets.length && datasets[0].data && datasets[0].data.length) {
			// get X axis labels from first data set (format x:, y:)
			return datasets[0].data.map((d: any) => d ? d.x : '');
		}
		return [];
	}

	setTooltipColors() {
		Object.assign(this.chartConfiguration.options.plugins.tooltip, {
			titleColor: '#555',
			bodyColor: this.bbChartSettings.bodyColor,
			borderColor: '#D8E5EE',
			borderWidth: '1px',
			backgroundColor: '#fff',
			displayColors: false,
			titleFont: {
				weight: 'normal'
			},
			bodyFont: {
				weight: 'bold',
				color: this.bbChartSettings.bodyColor
			},
			callbacks: {
				label: (context) => {
					let label = context.dataset.label || '';

					if (label) {
						label += ': ';
					}
					if (context.parsed.y !== null) {
						label += context.parsed.y;
					}
					return label;
				}
			}
		});
	}

	getOrCreateTooltip(chart: Chart) {
		let tooltipEl = chart.canvas.parentNode.querySelector('div');

		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.id = 'chartjs-tooltip';
			tooltipEl.style.background = '#FFFFFF';
			tooltipEl.style.borderRadius = '3px';
			tooltipEl.style.opacity = '1';
			tooltipEl.style.pointerEvents = 'none';
			tooltipEl.style.position = 'absolute';
			tooltipEl.style.transform = 'translate(10px, -10%)';
			tooltipEl.style.transition = 'all .1s ease';
			tooltipEl.style.border = '1px solid #D8E5EE';
			tooltipEl.style.boxShadow = '0px 1px 10px #50505035';

			const table = document.createElement('table');
			table.style.margin = '18px';

			tooltipEl.appendChild(table);
			chart.canvas.parentNode.appendChild(tooltipEl);
		}

		return tooltipEl;
	}

	externalTooltipHandler(context) {
		// Tooltip Element
		const chart: Chart = context.chart;
		const tooltip: TooltipModel<'line' | 'bar'> = context.tooltip;
		const tooltipEl = this.getOrCreateTooltip(chart);

		// Hide if no tooltip
		if (tooltip.opacity === 0) {
			tooltipEl.style.opacity = '0';
			return;
		}

		const tooltipIndex = tooltip.dataPoints[0].dataIndex;
		let extraTitle: string;

		if (tooltipIndex === 0 && this.bbChartSettings.tooltipFirstItem) {
			extraTitle = ` ${this.bbChartSettings.tooltipFirstItem}`;
		}
		if (tooltipIndex === this.datasets[0].data.length - 1 && this.bbChartSettings.tooltipLastItem) {
			extraTitle = ` ${this.bbChartSettings.tooltipLastItem}`;
		}

		// Set Text
		if (tooltip.body) {
			const titleLines = tooltip.title || [];
			const bodyLines = tooltip.body.map(b => b.lines);
			const tableHead = document.createElement('thead');

			titleLines.forEach(title => {
				// title
				const tr = document.createElement('tr');
				const th = document.createElement('th');
				th.colSpan = 2;
				th.style.color = this.bbChartSettings.tooltipTitleColor || '#8DABC4';
				th.style.textAlign = 'left';
				th.style.fontWeight = 'normal';
				th.textContent = title + (extraTitle || '');

				tr.appendChild(th);
				tableHead.appendChild(tr);

				// line
				const trLine = document.createElement('tr');
				const thLine = document.createElement('th');
				thLine.style.borderBottom = '1px solid #8DABC4';
				thLine.style.margin = '5px 0';
				thLine.style.minWidth = '25px';
				thLine.style.display = 'block';
				thLine.colSpan = 2;

				trLine.appendChild(thLine);
				tableHead.appendChild(trLine);
			});

			const tableBody = document.createElement('tbody');
			bodyLines.forEach((body, i) => {
				// body example: 'Views: 4'  or 'mediatype: video: 2'
				const colors = tooltip.labelColors[i];
				const indexStartValue = body[0].lastIndexOf(': ');
				const bodyViews = body[0].substr(indexStartValue + 2);

				const tr = document.createElement('tr');

				const tdValue = document.createElement('td');
				let color = colors.backgroundColor.toString();
				if (color === '[object CanvasGradient]') {
					// Bar chart performance data project will retreive gradient color which is not supported for text
					const dataPoint = tooltip?.dataPoints[0]?.raw as { hasKpiGoal: boolean; kpiGoalReached: boolean };
					if (dataPoint) {
						color = !dataPoint.hasKpiGoal ? BLUE : dataPoint.kpiGoalReached ? GREEN : ORANGE;
					}
				}
				tdValue.style.color = color;
				tdValue.style.fontWeight = 'bold';
				tdValue.style.paddingRight = '10px';
				tdValue.textContent = bodyViews;

				tr.appendChild(tdValue);

				const tdLabel = document.createElement('td');
				tdLabel.style.color = '#555555';
				tdLabel.textContent = body[0].substr(0, indexStartValue).toLowerCase();

				tr.appendChild(tdLabel);

				tableBody.appendChild(tr);
			});

			const tableRoot = tooltipEl.querySelector('table');

			// Remove old children
			while (tableRoot.firstChild) {
				tableRoot.firstChild.remove();
			}

			tableRoot.appendChild(tableHead);
			tableRoot.appendChild(tableBody);
		}

		const { offsetLeft: positionX, offsetTop: positionY, clientWidth: CanvasWidth } = chart.canvas;


		// Display tooltip to the right or left (positionX = padding horizontal)
		const leftBorderChart = chart.canvas.getBoundingClientRect().left;
		const remainingWidth = window.innerWidth - leftBorderChart - (positionX * 2) - tooltip.caretX - tooltipEl.clientWidth - 48;

		if (remainingWidth < 0) {
			tooltipEl.style.left = 'unset';
			tooltipEl.style.right = CanvasWidth - tooltip.caretX + positionX + 18 + 'px';
			tooltipEl.classList.remove('tooltip-pointer-right');
			tooltipEl.classList.add('tooltip-pointer-left');
		} else { // right
			tooltipEl.style.right = 'unset';
			tooltipEl.style.left = positionX + tooltip.caretX + 'px';
			tooltipEl.classList.remove('tooltip-pointer-left');
			tooltipEl.classList.add('tooltip-pointer-right');
		}

		// determine below/above data point
		const remainingHeightBelow = chart.canvas.clientHeight - tooltip.y;
		if (remainingHeightBelow < (tooltipEl.clientHeight * 1.2 )) {
			// above
			tooltipEl.style.setProperty('--caret-top', `${tooltip.height + 6}px`);
			tooltipEl.style.top = positionY + tooltip.caretY - 6 - tooltip.height + 'px'; // -6 to put it a bit higher
		} else {
			tooltipEl.style.setProperty('--caret-top', '6px');
			tooltipEl.style.top = positionY + tooltip.caretY - 6 + 'px'; // -6 to put it a bit higher
		}

		tooltipEl.style.padding = tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
		tooltipEl.style.opacity = '1';
	}
}
